import { apiFetch } from '@/lib/api';
import type {
  AdminStats,
  TenantBetaAccessResponse,
  TenantBetaAccessUpdate,
  TenantDetail,
  TenantListResponse,
  TenantStatusResponse,
  TenantStatusUpdate,
  WaitlistApproveResponse,
  WaitlistRejectResponse,
  WaitlistStats,
  WaitlistTenantItem,
  WaitlistWelcomeEmailResponse,
} from './types';

function withParams(path: string, params?: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.set(key, String(value));
  });
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

export const customerAdminApi = {
  getStats: (): Promise<AdminStats> => apiFetch('/api/v1/admin/stats'),

  listTenants: (params?: {
    status?: string;
    tier?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<TenantListResponse> => apiFetch(withParams('/api/v1/admin/tenants', params)),

  getTenantDetail: (tenantId: string): Promise<TenantDetail> =>
    apiFetch(`/api/v1/admin/tenants/${encodeURIComponent(tenantId)}`),

  updateTenantStatus: (tenantId: string, payload: TenantStatusUpdate): Promise<TenantStatusResponse> =>
    apiFetch(`/api/v1/admin/tenants/${encodeURIComponent(tenantId)}/status`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  updateTenantBetaAccess: (
    tenantId: string,
    payload: TenantBetaAccessUpdate,
  ): Promise<TenantBetaAccessResponse> =>
    apiFetch(`/api/v1/admin/tenants/${encodeURIComponent(tenantId)}/beta-access`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  sendAccessEmail: (
    tenantId: string,
  ): Promise<{ success: boolean; tenant_id: string; sent_to: string; message: string }> =>
    apiFetch(`/api/v1/admin/tenants/${encodeURIComponent(tenantId)}/send-access-email`, {
      method: 'POST',
    }),
};

/**
 * Director waitlist operations. Proxied by the Seitiate+ backend to
 * sakme-backend (the operational waitlist home); the ids here are the
 * waitlist row UUIDs from `getPending`, not engine tenant slugs. When the
 * bridge is unconfigured the proxy answers 503 — surface it honestly.
 */
export const waitlistAdminApi = {
  getPending: (): Promise<WaitlistTenantItem[]> => apiFetch('/api/v1/admin/waitlist/pending'),

  getStats: (): Promise<WaitlistStats> => apiFetch('/api/v1/admin/waitlist/stats'),

  approve: (tenantUuid: string): Promise<WaitlistApproveResponse> =>
    apiFetch(`/api/v1/admin/waitlist/${encodeURIComponent(tenantUuid)}/approve`, {
      method: 'POST',
    }),

  reject: (tenantUuid: string): Promise<WaitlistRejectResponse> =>
    apiFetch(`/api/v1/admin/waitlist/${encodeURIComponent(tenantUuid)}/reject`, {
      method: 'POST',
    }),

  sendWelcomeEmail: (tenantUuid: string): Promise<WaitlistWelcomeEmailResponse> =>
    apiFetch(`/api/v1/admin/waitlist/${encodeURIComponent(tenantUuid)}/send-welcome-email`, {
      method: 'POST',
    }),
};
